(() => {
  'use strict';
  // Route selects a protocol; browser persistence cannot activate prepaid.
  const prepaidRoute = window.TAC_CUSTOMIZER_ROUTE === 'prepaid';
  let authorizedPrepaid = false;
  Object.defineProperty(window, 'TAC_PREPAID_ACTIVE', {configurable: false, get: () => authorizedPrepaid});
  if (!prepaidRoute) { window.TACPrepaidReady = Promise.resolve(null); return; }
  // Capture only in this closure and clear before our first asynchronous operation.
  const incoming = new URLSearchParams(location.hash.slice(1));
  if (['tac_prepaid_start', 'tac_prepaid_handoff', 'tac_receiver', 'tac_verifier'].some(key => incoming.has(key))) {
    history.replaceState(null, '', location.pathname + location.search);
  }
  const runtime = window.TAC_PREPAID_RUNTIME;
  const environment = String(runtime?.environment || '');
  const API = String(runtime?.api_base_url || '').replace(/\/+$/, '');
  const expectedApi = environment === 'production'
    ? 'https://tac-prepaid-api.uralbum-cool.workers.dev'
    : environment === 'staging'
      ? 'https://tac-prepaid-api-staging.uralbum-cool.workers.dev'
      : '';
  if (!expectedApi || API !== expectedApi) throw new Error('prepaid_environment_not_configured');
  // A restored document has no receiver-start closure. Its return destination
  // comes from theme configuration, never from persisted design credentials.
  let accountPage = null;
  if (runtime.customer_account_page_url) {
    const candidate = new URL(runtime.customer_account_page_url);
    const validPath = environment === 'production'
      ? candidate.hostname === 'account.tusalbumcool.com' && /^\/pages\/[0-9a-f-]{36}$/i.test(candidate.pathname)
      : candidate.hostname === 'shopify.com' && /^\/\d+\/account\/pages\/[0-9a-f-]{36}$/i.test(candidate.pathname);
    if (candidate.protocol !== 'https:' || candidate.username || candidate.password || candidate.search || candidate.hash || !validPath) {
      throw new Error('prepaid_account_url_invalid');
    }
    accountPage = candidate.toString();
    window.TAC_PREPAID_ACCOUNT_URL = accountPage;
  }
  const handle = String(window.TAC_SHOPIFY_CONFIG?.productHandle || '').toLowerCase();
  const filesApi = environment === 'production' ? 'https://tac-files-api.uralbum-cool.workers.dev' : 'https://tac-files-api-staging.uralbum-cool.workers.dev';
  let blocked = false, paused;
  function pauseForAccount() {
    if (paused) return paused;
    blocked = true;
    paused = new Promise(() => {}); // Keep pending File selections in this document until the user leaves.
    clearTimeout(window.tacDraftClient?.retryTimer);
    clearTimeout(window.tacDraftClient?.saveTimer);
    if (window.tacDraftClient) window.tacDraftClient.saveQueued = false;
    const show = () => {
      const english = String(window.tacLang || 'es').startsWith('en');
      const box = document.createElement('section'); box.id = 'tac-prepaid-access';
      box.setAttribute('role', 'status'); box.setAttribute('aria-live', 'polite');
      box.style.cssText = 'position:fixed;bottom:12px;left:12px;right:12px;z-index:2147483647;max-height:45vh;overflow:auto;padding:16px;background:#fff8f0;color:#34282c;border:2px solid #9f4c5d;border-radius:12px;box-shadow:0 4px 24px #0003;font:16px/1.5 system-ui';
      const title = document.createElement('strong');
      title.textContent = english ? 'Your album is waiting for you!' : '¡Tu álbum te está esperando!';
      const message = document.createElement('p');
      message.textContent = english ? 'Your saved progress is still here. Go to your account to keep personalizing.' : 'Tus avances guardados siguen aquí. Entra a tu cuenta para continuar personalizando.';
      const pendingNote = document.createElement('p');
      pendingNote.textContent = english ? 'Going to your account closes this editor. Photos still waiting to upload and unsaved edits will not be kept.' : 'Al entrar a tu cuenta se cerrará este editor. Las fotos pendientes de subir y los cambios sin guardar no se conservarán.';
      box.append(title, message, pendingNote);
      if (accountPage) {
        const button = document.createElement('button'); button.type = 'button';
        button.textContent = english ? 'Continue my album' : 'Continuar mi álbum';
        button.style.cssText = 'min-height:44px;padding:8px 16px;font:inherit;cursor:pointer';
        button.onclick = () => location.assign(accountPage); // Existing Customer Accounts path, same tab; no credentials in URL.
        box.append(button);
      }
      document.body.append(box);
      document.dispatchEvent(new CustomEvent('tac:prepaid-access', {detail: {blocked: true}}));
    };
    if (document.body) show(); else document.addEventListener('DOMContentLoaded', show, {once: true});
    return paused;
  }
  window.TACPrepaidAccess = {
    get blocked() { return blocked; },
    async fetch(session, url, options) {
      const workerRequest = session?.access_mode === 'prepaid' &&
        String(url).startsWith(filesApi + '/v1/drafts/' + encodeURIComponent(session.id)) &&
        new URL(url).pathname.match(/^\/v1\/drafts\/DR-[0-9a-f-]{36}(?:\/|$)/i);
      if (!workerRequest) return fetch(url, options);
      if (blocked) return paused;
      // The server owns idle expiry. Never use the draft-retention date or an old browser expiry to deny active work.
      const response = await fetch(url, options);
      if ([401, 403, 404].includes(response.status)) {
        const body = await response.clone().json().catch(() => null);
        if (body?.error === 'draft_not_found_or_unauthorized') return pauseForAccount();
      }
      return response;
    }
  };
  // P1-C: receiver-bound proof only. No handoff or session credential belongs here.
  window.addEventListener('hashchange', () => {
    const fragment = new URLSearchParams(location.hash.slice(1));
    if (['tac_prepaid_start', 'tac_prepaid_handoff', 'tac_receiver', 'tac_verifier'].some(key => fragment.has(key))) {
      history.replaceState(null, '', location.pathname + location.search);
      // Do not replace a live editor/session from a fragment-only navigation.
      // Keep its in-memory work and require a new normal Accounts journey.
      pauseForAccount();
    }
  });
  const proofDatabase = 'tac-prepaid-receiver-proof-v1';
  const proofStore = 'receivers';
  const proofLifetime = 300000;
  const entryViews = Object.freeze({
    'album-fondo-fotos-basic-30-estampas': 'tac-prepaid-small',
    'album-fondo-fotos-basic-50-estampas': 'tac-prepaid-medium',
    'album-fondo-fotos-pro-50-estampas': 'tac-prepaid-plus',
    'album-fondo-fotos-super-pro-50-estampas': 'tac-prepaid-super-plus'
  });
  const receiverPath = () => {
    const view = entryViews[handle];
    const path = view && '/?view=' + view + (environment === 'staging' ? '&preview_theme_id=160913031215' : '');
    if (location.origin !== 'https://tusalbumcool.com' || !path || location.pathname + location.search !== path) {
      throw new Error('prepaid_return_invalid');
    }
    return path;
  };
  function openProofDatabase() {
    return new Promise((resolve, reject) => {
      let settled = false, request;
      const fail = () => { if (!settled) { settled = true; clearTimeout(timer); reject(new Error('prepaid_proof_storage')); } };
      const timer = setTimeout(fail, 5000);
      try { request = indexedDB.open(proofDatabase, 1); } catch { fail(); return; }
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(proofStore)) request.result.createObjectStore(proofStore, {keyPath: 'receiver'});
      };
      request.onerror = fail;
      request.onblocked = fail;
      request.onsuccess = () => {
        const db = request.result;
        if (settled) { db.close(); return; }
        settled = true; clearTimeout(timer);
        db.onversionchange = () => db.close();
        resolve(db);
      };
    });
  }
  async function proofTransaction(mode, operation) {
    const db = await openProofDatabase();
    return new Promise((resolve, reject) => {
      let transaction, value, settled = false;
      const finish = ok => {
        if (settled) return;
        settled = true; clearTimeout(timer); db.close();
        if (ok) resolve(value); else reject(new Error('prepaid_proof_storage'));
      };
      const timer = setTimeout(() => { try { transaction?.abort(); } catch {} finish(false); }, 5000);
      try {
        transaction = db.transaction(proofStore, mode);
        transaction.oncomplete = () => finish(true); // A successful put alone is insufficient.
        transaction.onabort = transaction.onerror = () => finish(false);
        operation(transaction.objectStore(proofStore), result => { value = result; });
      } catch { try { transaction?.abort(); } catch {} finish(false); }
    });
  }
  const validProofTime = (record, now = Date.now()) => Number.isFinite(record?.created_at) &&
    Number.isFinite(record?.expires_at) && record.expires_at > record.created_at &&
    record.expires_at <= record.created_at + proofLifetime && now >= record.created_at && now < record.expires_at;
  const cleanExpiredProofs = () => proofTransaction('readwrite', store => {
    const cursor = store.openCursor();
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (!current) return;
      if (!validProofTime(current.value)) current.delete();
      current.continue();
    };
  });
  const saveProof = record => proofTransaction('readwrite', store => store.add(record));
  const removeProof = receiver => proofTransaction('readwrite', store => store.delete(receiver));
  const loadProof = receiver => proofTransaction('readonly', (store, set) => {
    const request = store.get(receiver);
    request.onsuccess = () => set(request.result);
  });

  const sessionKey = `tac:prepaid-design-session:v1:${handle}`;
  const base64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  const hex = bytes => [...new Uint8Array(bytes)].map(value => value.toString(16).padStart(2, '0')).join('');
  const post = async (path, body) => {
    const response = await fetch(API + path, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.ok) throw new Error(result?.error || `prepaid_${response.status}`);
    return result;
  };
  const activatePrepaidSession = session => {
    authorizedPrepaid = true;
    window.TAC_PREPAID_AUTO_OPEN = true;
    window.dispatchEvent(new CustomEvent('tac:prepaid-session-ready', {detail: {product_handle: session.product_handle}}));
    return session;
  };
  const validateStoredSession = async stored => {
    const filesApi = environment === 'production'
      ? 'https://tac-files-api.uralbum-cool.workers.dev'
      : 'https://tac-files-api-staging.uralbum-cool.workers.dev';
    const response = await fetch(filesApi + '/v1/drafts/' + encodeURIComponent(stored.id), {
      headers: {Authorization: 'Bearer ' + stored.token, Accept: 'application/json'}, cache: 'no-store'
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.ok || result.draft?.access_mode !== 'prepaid' ||
        result.draft.id !== stored.id || result.draft.product_handle !== handle) {
      throw new Error('prepaid_channel_not_authorized');
    }
    return activatePrepaidSession({...stored, revision: result.draft.revision, access_mode: result.draft.access_mode});
  };
  window.TACPrepaidReady = (async () => {
    if (incoming.has('tac_verifier')) throw new Error('prepaid_return_invalid');
    const keys = [...incoming.keys()];
    const entitlementId = incoming.get('tac_prepaid_start');
    if (incoming.has('tac_prepaid_start')) {
      if (keys.length !== 1 || !/^PE-[0-9a-f-]{36}$/i.test(entitlementId || '') || !accountPage) throw new Error('prepaid_return_invalid');
      const returnPath = receiverPath();
      await cleanExpiredProofs();
      const createdAt = Date.now(), started = performance.now();
      const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
      const challengeHash = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
      const created = await post('/public/v1/receiver-transactions', {challenge_hash: challengeHash, return_path: returnPath});
      if (created?.protocol_version !== 2 || !/^PR-[0-9a-f-]{36}$/i.test(created?.receiver_transaction_id || '') ||
          !Number.isFinite(created.expires_at) || new URL(created.customer_account_page_url).toString() !== accountPage) {
        throw new Error('prepaid_receiver_invalid');
      }
      // The client never extends its own 300-second cap; SQL remains authoritative.
      const expiresAt = Math.min(createdAt + proofLifetime, created.expires_at * 1000);
      const record = {receiver: created.receiver_transaction_id, verifier, created_at: createdAt, expires_at: expiresAt,
        origin: location.origin, environment, return_path: returnPath, entitlement_id: entitlementId};
      if (!validProofTime(record) || performance.now() - started >= proofLifetime) throw new Error('prepaid_proof_expired');
      await saveProof(record);
      if (!validProofTime(record) || performance.now() - started >= proofLifetime) {
        await removeProof(record.receiver).catch(() => {});
        throw new Error('prepaid_proof_expired');
      }
      const accountUrl = new URL(accountPage);
      accountUrl.searchParams.set('tac_receiver', record.receiver);
      accountUrl.searchParams.set('tac_entitlement', entitlementId);
      accountUrl.searchParams.set('tac_challenge', challengeHash); // Public challenge, never V or a bearer.
      location.assign(accountUrl.toString());
      return new Promise(() => {}); // No editor initialization while leaving for Accounts.
    }
    if (incoming.has('tac_prepaid_handoff') || incoming.has('tac_receiver')) {
      const handoff = incoming.get('tac_prepaid_handoff'), receiver = incoming.get('tac_receiver');
      if (keys.length !== 2 || !keys.includes('tac_prepaid_handoff') || !keys.includes('tac_receiver') ||
          !/^[A-Za-z0-9_-]{43}$/.test(handoff || '') || !/^PR-[0-9a-f-]{36}$/i.test(receiver || '')) throw new Error('prepaid_return_invalid');
      const returnPath = receiverPath();
      await cleanExpiredProofs();
      const record = await loadProof(receiver);
      if (!record || record.receiver !== receiver || record.origin !== location.origin || record.environment !== environment ||
          record.return_path !== returnPath || !/^PE-[0-9a-f-]{36}$/i.test(record.entitlement_id || '') ||
          !/^[A-Za-z0-9_-]{43}$/.test(record.verifier || '') || !validProofTime(record)) throw new Error('prepaid_receiver_proof_missing');
      const redeemed = await post('/theme/v1/design-sessions/redeem', {receiver_transaction_id: receiver, handoff, verifier: record.verifier, expected_entitlement_id: record.entitlement_id, protocol_version: 2});
      // A lost response never triggers another redeem automatically. Single-use stays intact.
      await removeProof(receiver).catch(() => {}); // If cleanup fails, server consumption still forbids replay.
      if (redeemed?.session?.access_mode !== 'prepaid' || String(redeemed.session.product_handle || '').toLowerCase() !== handle ||
          !/^DR-[0-9a-f-]{36}$/i.test(redeemed.session.draft_id || '') || !/^[A-Za-z0-9_-]{43}$/.test(redeemed.session.token || '')) {
        throw new Error('prepaid_product_mismatch');
      }
      const session = {id: redeemed.session.draft_id, token: redeemed.session.token, revision: redeemed.session.revision,
        material_version: redeemed.session.material_version, claim_version: redeemed.session.claim_version,
        expires_at: redeemed.session.expires_at, product_handle: redeemed.session.product_handle, access_mode: 'prepaid'};
      sessionStorage.setItem(sessionKey, JSON.stringify(session));
      return activatePrepaidSession(session);
    }
    const stored = JSON.parse(sessionStorage.getItem(sessionKey) || 'null');
    if (stored?.id && stored?.token && stored?.access_mode === 'prepaid') return validateStoredSession(stored);
    return pauseForAccount();
  })().catch(() => {
    // Never log arbitrary exception/response text: it may include sensitive navigation data.
    return pauseForAccount();
  });

  const submissionKey = draftId => `tac:prepaid-submission-key:v1:${draftId}`;
  const submissionPayloadKey = draftId => `tac:prepaid-submission-payload:v1:${draftId}`;
  const submissionIntentKey = draftId => `tac:prepaid-confirmed-intent:v1:${draftId}`;
  const reviewProofs = new WeakMap();
  let reviewSequence = 0;
  let submissionRequest = null;
  const fingerprintHash = async fingerprint => hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(fingerprint)));
  const submissionPayload = review => ({expected_revision:Number(review.draft_revision),expected_material_version:Number(review.material_version),
    claim_version:Number(review.claim_version),expected_input_hash:String(review.input_hash),
    expected_snapshot_hash:String(review.snapshot_hash),expected_package_rules_version:String(review.package_rules_version || ''),
    expected_rules_version:String(review.rules_version || ''),confirmed:true});
  const responseError = (code, status) => {
    const error = new Error(code);
    error.tacCode = code;
    error.status = Number(status || 0);
    return error;
  };
  window.TACPreparePrepaidReview = async () => {
    const session = await window.TACPrepaidReady;
    if (!session?.id || !session?.token || session.access_mode !== 'prepaid') throw new Error('prepaid_session_missing');
    const sequence = ++reviewSequence;
    if (!await window.TACFlushDraftForReview?.()) throw responseError('prepaid_save_not_ready', 409);
    const barrier = window.TACPrepaidSaveBarrier, proof = barrier?.capture();
    if (!proof || proof.id !== session.id || proof.session.token !== session.token) throw responseError('prepaid_save_not_ready', 409);
    const response = await fetch(`${API}/theme/v1/drafts/${encodeURIComponent(session.id)}/submission-review`, {
      method: 'POST', headers: {Authorization: `TAC-Prepaid ${session.token}`, 'Content-Type': 'application/json', Accept: 'application/json'},
      body: '{}', cache: 'no-store'
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.review) {
      throw responseError(result?.error || `prepaid_review_${response.status}`, response.status);
    }
    const review = result.review;
    if (sequence !== reviewSequence || !barrier.matches(proof) ||
        Number(review.draft_revision) !== proof.revision || !Number.isInteger(Number(review.material_version)) ||
        (proof.claimVersion && Number(review.claim_version) !== proof.claimVersion) || barrier.fingerprint(review.configuration) !== proof.fingerprint ||
        !barrier.matchesInventory(review.file_inventory, proof)) {
      throw responseError('prepaid_review_changed', 409);
    }
    reviewProofs.set(review, {...proof, reviewFingerprint: barrier.fingerprint(review)});
    return review;
  };
  const sendSubmission = async (session, key, payload) => {
    if (submissionRequest) return submissionRequest;
    submissionRequest = (async () => {
      const response = await fetch(`${API}/theme/v1/drafts/${encodeURIComponent(session.id)}/submission`, {
        method: 'POST',
        headers: {Authorization: `TAC-Prepaid ${session.token}`, 'Content-Type': 'application/json', Accept: 'application/json', 'Idempotency-Key': key},
        body: JSON.stringify(payload), cache: 'no-store'
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.submission) {
        if (response.status === 409 || response.status === 422) {
          sessionStorage.removeItem(submissionKey(session.id));
          sessionStorage.removeItem(submissionPayloadKey(session.id));
          sessionStorage.removeItem(submissionIntentKey(session.id));
        }
        throw responseError(result?.error || `prepaid_submission_${response.status}`, response.status);
      }
      return result.submission;
    })();
    try { return await submissionRequest; }
    finally { submissionRequest = null; }
  };
  window.TACSubmitPrepaidDesign = async review => {
    const session = await window.TACPrepaidReady;
    if (!session?.id || !session?.token || session.access_mode !== 'prepaid') throw new Error('prepaid_session_missing');
    if (!review || !Number.isInteger(Number(review.draft_revision)) || !Number.isInteger(Number(review.material_version)) ||
      !Number.isInteger(Number(review.claim_version)) || !/^[0-9a-f]{64}$/.test(String(review.input_hash || '')) ||
      !/^[0-9a-f]{64}$/.test(String(review.snapshot_hash || ''))) throw new Error('prepaid_review_missing');
    const barrier = window.TACPrepaidSaveBarrier, proof = reviewProofs.get(review);
    if (!barrier?.matches(proof) || proof.id !== session.id || proof.session.token !== session.token || proof.reviewFingerprint !== barrier.fingerprint(review)) {
      throw responseError('prepaid_save_not_ready', 409);
    }
    const payload = submissionPayload(review), encodedPayload = JSON.stringify(payload);
    const contentHash = await fingerprintHash(proof.fingerprint);
    // No await between this final gate and storing/dispatching the confirmed intent.
    if (!barrier.matches(proof)) throw responseError('prepaid_save_not_ready', 409);
    const previous = sessionStorage.getItem(submissionPayloadKey(session.id));
    if (previous && previous !== encodedPayload) throw responseError('prepaid_submission_pending', 409);
    let key = sessionStorage.getItem(submissionKey(session.id));
    if (previous && !key) throw responseError('prepaid_submission_pending', 409);
    if (!key) key = `submit-${crypto.randomUUID()}`;
    sessionStorage.setItem(submissionKey(session.id), key);
    sessionStorage.setItem(submissionPayloadKey(session.id), encodedPayload);
    sessionStorage.setItem(submissionIntentKey(session.id), JSON.stringify({key, payload:encodedPayload, contentHash}));
    return sendSubmission(session, key, payload);
  };
  window.TACResumePrepaidSubmission = async () => {
    const session = await window.TACPrepaidReady;
    if (!session?.id || !session?.token || session.access_mode !== 'prepaid') throw new Error('prepaid_session_missing');
    const key = sessionStorage.getItem(submissionKey(session.id));
    const payload = JSON.parse(sessionStorage.getItem(submissionPayloadKey(session.id)) || 'null');
    if (!key || !payload) return null;
    const intent = JSON.parse(sessionStorage.getItem(submissionIntentKey(session.id)) || 'null');
    const barrier = window.TACPrepaidSaveBarrier, proof = barrier?.capture();
    // Legacy/unproven local payloads require a fresh visible review and confirmation.
    if (!intent || intent.key !== key || intent.payload !== JSON.stringify(payload) || !proof) return null;
    if (await fingerprintHash(proof.fingerprint) !== intent.contentHash || !barrier.matches(proof)) {
      throw responseError('prepaid_save_not_ready', 409);
    }
    return sendSubmission(session, key, payload);
  };
  window.TACHasPendingPrepaidSubmission = () => {
    const session = JSON.parse(sessionStorage.getItem(sessionKey) || 'null');
    return Boolean(session?.id && sessionStorage.getItem(submissionKey(session.id)) && sessionStorage.getItem(submissionPayloadKey(session.id)));
  };
  document.addEventListener('click', event => {
    if (!window.TAC_PREPAID_ACTIVE || !event.target?.closest?.('.tac-addcart-btn')) return;
    event.preventDefault(); event.stopImmediatePropagation();
  }, true);
})();
